import type {
  BatchDetailResponse,
  Proposal,
  ProposalListResponse,
} from '../../../lib/api/proposals/proposals-api';

/**
 * Statuses the batch approve/deny endpoints skip. Everything else in the batch
 * is acted on, in every brand the employee can access, regardless of the list
 * filters or the page the reviewer is looking at.
 */
export const BATCH_SKIPPED_STATUSES = new Set([
  'approved',
  'denied',
  'executing',
  'completed',
  'failed',
]);

export interface BatchScope {
  /** Open proposals the batch verb will act on. */
  items: Proposal[];
  /** Distinct brand names of those proposals, in first-seen order. */
  brands: string[];
  /** Open proposals not visible in the current list (filter or page). */
  hiddenCount: number;
}

export function summarizeBatchScope(
  batch: BatchDetailResponse,
  visibleIds: Iterable<string>
): BatchScope {
  const visible = new Set(visibleIds);
  const items = (batch.items ?? []).filter(
    (p) => !BATCH_SKIPPED_STATUSES.has(p.status)
  );
  const brands: string[] = [];
  for (const p of items) {
    const name = p.shop?.name || p.shop?.slug;
    if (name && !brands.includes(name)) brands.push(name);
  }
  const hiddenCount = items.filter((p) => !visible.has(p.id)).length;
  return { items, brands, hiddenCount };
}

export type BatchScopeState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; scope: BatchScope };

/** The batch confirm button only fires once the real scope is known. */
export function batchConfirmBlocked(state: BatchScopeState | null): boolean {
  return !state || state.status !== 'ready' || state.scope.items.length === 0;
}

// central-api caps per_page at 100 on the employee proposals index, so the
// per-brand strip pages through the pending list at that size.
export const PER_SHOP_COUNTS_PAGE_SIZE = 100;
// Upper bound so a very large queue can't page forever (2,000 proposals).
export const PER_SHOP_COUNTS_MAX_PAGES = 20;

export interface PerShopCount {
  name: string;
  count: number;
}

/**
 * Pages through the pending_review list and groups it per brand. `total`
 * comes from meta.total so it stays right even if the page bound is hit.
 */
export async function fetchPendingCountsByShop(
  list: (filters: {
    status: 'pending_review';
    page: number;
    per_page: number;
  }) => Promise<ProposalListResponse>,
  maxPages: number = PER_SHOP_COUNTS_MAX_PAGES
): Promise<{ grouped: Map<string, PerShopCount>; total: number }> {
  const grouped = new Map<string, PerShopCount>();
  let total = 0;
  let seen = 0;
  for (let page = 1; page <= maxPages; page += 1) {
    // eslint-disable-next-line no-await-in-loop
    const result = await list({
      status: 'pending_review',
      page,
      per_page: PER_SHOP_COUNTS_PAGE_SIZE,
    });
    const rows = result.data ?? [];
    seen += rows.length;
    total = result.meta?.total ?? seen;
    for (const p of rows) {
      const slug = p.shop?.slug;
      if (!slug) continue;
      const existing = grouped.get(slug);
      if (existing) {
        existing.count += 1;
      } else {
        grouped.set(slug, { name: p.shop?.name || slug, count: 1 });
      }
    }
    const lastPage = result.meta?.last_page ?? 1;
    if (page >= lastPage || rows.length === 0) break;
  }
  return { grouped, total };
}
